import * as React from "react";
import { Block } from "../Block";
import { Layer } from "baseui/layer";
import { Portal } from "react-portal";
import { SizeMe } from "react-sizeme";
import { styled } from "baseui";
import { useIsScrolledToBottom } from "../useScrollPosition";
import { useUnderBreakpoint } from "../useUnderBreakpoint";

/**
 * For fixing content to the bottom of the viewport. Currently at mobile resolutions
 * only; uses the "medium" breakpoint as found in the BaseWeb theme.
 *
 * Uses JavaScript instead of a CSS breakpoint to determine whether to be fixed.
 * This is because we need to render a different wrapper (Layer), as well as apply
 * shadow/fader conditionally based on scroll, so it might as well all be done by JS.
 *
 * NOTE: Due to the use of BaseWeb Layers, this component will render at the bottom
 * of the body when it is fixed. This will require form submit buttons to have an
 * explicit onClick as they will have been removed from their parent form.
 */
const FixedFooter: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const isFixed = useUnderBreakpoint("medium");
  const isBottomed = useIsScrolledToBottom();
  const LayerWrapper = isFixed ? Layer : React.Fragment;

  return (
    <LayerWrapper>
      <SizeMe monitorHeight={true} monitorWidth={false}>
        {({ size }) => (
          <Footer $isFixed={isFixed} $showFade={isFixed && !isBottomed}>
            {children}
            {isFixed && (
              // Insert a spacemaker at the bottom of body, so that fixed content does not overlay anything
              <Portal>
                <Block height={`${size.height}px`} />
              </Portal>
            )}
          </Footer>
        )}
      </SizeMe>
    </LayerWrapper>
  );
};

type TFooterProps = {
  $showFade?: boolean;
  $isFixed?: boolean;
};

const Footer = styled<"div", TFooterProps>(
  "div",
  ({ $theme, $showFade, $isFixed }) => ({
    backgroundColor: $isFixed ? $theme.colors.backgroundPrimary : "none",
    position: $isFixed ? "fixed" : undefined,
    left: 0,
    right: 0,
    bottom: 0,
    transitionProperty: "box-shadow",
    transitionDuration: $theme.animation.timing300,
    transitionTimingFunction: "linear",
    boxShadow: $showFade ? "0 0 48px 5px rgba(0,0,0,0.06)" : undefined,
  })
);

export { FixedFooter };
